import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, ImageRun, AlignmentType, Table, TableRow, TableCell, WidthType,ExternalHyperlink ,UnderlineType} from "docx";

const exportSlateToDocx = async(url, filename, setLoader) => {
  try {
    setLoader(true);
    const response = await fetch(url);
    const jsonData = await response.json();
    const slateContent = jsonData?.content;

    if (!slateContent || !Array.isArray(slateContent)) {
      console.error("Invalid Slate.js content");
      setLoader(false);
      return;
    }

    const blocks = await Promise.all(slateContent.map((block) => convertBlockToParagraph(block)));

    const doc = new Document({
      sections: [
        {
          properties: {},
          children: blocks
        }
      ]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, filename);
  } catch (error) {
    console.error("DOCX Export Error:", error);
  } finally {
    setLoader(false);
  }
};

const fetchImageAsBuffer = async(url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return await blob.arrayBuffer();
};

const convertBlockToParagraph = async(block) => {
  switch (block.type) {
  case "heading-one":
    return new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: block.children.map(formatTextRun)
    });

  case "heading-two":
    return new Paragraph({
      heading: HeadingLevel.HEADING_2,
      children: block.children.map(formatTextRun)
    });

  case "bulleted-list":
    return new Paragraph({
      bullet: { level: 0 },
      children: block.children.map(formatTextRun)
    });

  case "numbered-list":
    return new Paragraph({
      numbering: { reference: "numbered", level: 0 },
      children: block.children.map(formatTextRun)
    });

  case "block-quote": {
      // Map children safely — flatten is fine here
    const textRuns = block.children.flatMap(formatTextRun);

      // Use typographic quotes and style them (optional)
    const quotedRuns = [
      new TextRun({
        text: "“",
        italics: true,
        color: "666666"
      }),
      ...textRuns,
      new TextRun({
        text: "”",
        italics: true,
        color: "666666"
      })
    ];

    return new Paragraph({
      children: quotedRuns,
      indent: { left: 720 }, // 720 twentieths = 0.5 inch indent, more visible
      spacing: { before: 200, after: 200 }, // add space before and after paragraph
      alignment: block.align ? getAlignment(block.align) : AlignmentType.LEFT,
      style: "Quote" // optional, if you define a Quote style in docx theme
    });
  }

  case "code-block": {
    const maxCharsPerLine = 85;
    const textContent = getBlockText(block);
    const wrappedText = (
      textContent.match(new RegExp(`.{1,${maxCharsPerLine}}`, "g")) || [
        textContent
      ]
    ).join("\n");

    return new Paragraph({
      children: [
        new TextRun({
          text: wrappedText,
          font: "Courier New"
        })
      ]
    });
  }

  case "link-preview":
    return new Paragraph({
      children: [
        new TextRun({
          text: block.title || "Link Title",
          bold: true,
          break: 1
        }),
        new TextRun({
          text: block.description || "Link description goes here...",
          break: 1
        }),
        new ExternalHyperlink({
          children: [
            new TextRun({
              text: "Open Link",
              style: "Hyperlink"
            })
          ],
          link: block.url
        })
      ]
    });

  case "image":
    const imageBuffer = await fetchImageAsBuffer(block.url);
    return new Paragraph({
      children: [
        new ImageRun({
          data: imageBuffer,
          transformation: {
            width: block.width || 400,
            height: block.height || 300
          }
        })
      ]
    });

  case "table":
    return convertTable(block);

  default:
    return new Paragraph({
      children: block.children.map(formatTextRun),
      alignment: block.align ? getAlignment(block.align) : AlignmentType.LEFT
    });
  }
};

// sir code
// const formatTextRun = (child) => {
//   return new TextRun({
//     text: child.text || "",
//     bold: child.bold || false,
//     italics: child.italic || false,
//     underline: child.underline ? "single" : undefined,
//     strike: child.strikethrough || false,
//     color: child.color || "000000",
//     highlight: child.highlight ? "yellow" : undefined
//   });
// };


// const convertTable = (block) => {
//   return new Table({
//     width: { size: 100, type: WidthType.PERCENTAGE },
//     rows: block.rows.map((row) =>
//       new TableRow({
//         children: row.cells.map(
//           (cell) =>
//             new TableCell({
//               children: [new Paragraph({ children: cell.children.map(formatTextRun) })]
//             })
//         )
//       })
//     )
//   });
// };

// <-------------------------->

const formatTextRun = (child) => {
  if (!child) return new TextRun("");

  const runOptions = {
    text: child.text || "",
    bold: !!child.bold,
    italics: !!child.italic || !!child.italics,
    underline: child.underline ? { type: UnderlineType.SINGLE } : undefined,
    strike: !!child.strikethrough || !!child.strike,
    style: child.link ? "Hyperlink" : undefined
  };

  const bgColor = normalizeColor(
    child.backgroundColor || child.style?.backgroundColor,
    null
  );
  if (bgColor) {
    runOptions.shading = {
      type: "clear",
      color: "auto",
      fill: bgColor
    };
  }

  const textColor = normalizeColor(child.color || child.style?.color, "000000");
  if (textColor) {
    runOptions.color = textColor;
  }

  const run = new TextRun(runOptions);

  if (
    child.link &&
    typeof child.url === "string" &&
    child.url.startsWith("http")
  ) {
    return new ExternalHyperlink({
      children: [
        new TextRun({
          text: child.text || child.url,
          style: "Hyperlink"
        })
      ],
      link:child.url
    });
  }

  return run;
};

function normalizeColor(value, fallback = "FFFF00") {
  if (!value || typeof value !== "string") return fallback;
  value = value.trim();

  // #RRGGBB or #RGB
  if (value.startsWith("#")) {
    const hex = value.slice(1);
    if (/^[0-9A-Fa-f]{6}$/.test(hex)) return hex.toUpperCase();
    if (/^[0-9A-Fa-f]{3}$/.test(hex)) {
      return hex
        .split("")
        .map((c) => c + c)
        .join("")
        .toUpperCase();
    }
  }

  // rgb() / rgba()
  if (value.startsWith("rgb")) {
    const nums = value.match(/\d+/g);
    if (nums && nums.length >= 3) {
      const r = Math.min(255, parseInt(nums[0], 10))
        .toString(16)
        .padStart(2, "0");
      const g = Math.min(255, parseInt(nums[1], 10))
        .toString(16)
        .padStart(2, "0");
      const b = Math.min(255, parseInt(nums[2], 10))
        .toString(16)
        .padStart(2, "0");
      return `${r}${g}${b}`.toUpperCase();
    }
  }

  return fallback;
}

function getBlockText(block) {
  if (!block?.children) return "";
  return block.children.map((child) => child.text || "").join("");
}

const convertTable = (block) => {
  const rowsFromChildren = block.children;
  if (!rowsFromChildren || !Array.isArray(rowsFromChildren)) {
    const errorMessage = `Invalid table block: 'rows' is undefined or malformed`;
    console.error(errorMessage, block);
    return new Paragraph({
      children: [
        new TextRun({ text: errorMessage, bold: true, color: "FF0000" }),
        new TextRun({
          text: JSON.stringify(block, null, 2),
          break: 1,
          size: 20
        })
      ]
    });
  }

  const rows = rowsFromChildren.map((rowNode, rowIndex) => {
    const cells = (rowNode.children || []).map((cellNode) => {
      const paras = [];

      const cellChildren = Array.isArray(cellNode.children)
        ? cellNode.children
        : [];

      if (cellChildren.length) {
        for (const childBlock of cellChildren) {
          // childBlock can be a paragraph-like node (with .children leaves),
          // or could be a simple leaf with .text
          if (
            Array.isArray(childBlock.children) &&
            childBlock.children.length
          ) {
            // Build runs from leaves using your formatTextRun (which may return TextRun or ExternalHyperlink)
            const runs = childBlock.children.flatMap((leaf) => {
              const formatted = formatTextRun(leaf);
              // formatTextRun may return a single node (TextRun or ExternalHyperlink)
              return Array.isArray(formatted) ? formatted : [formatted];
            });

            paras.push(
              new Paragraph({
                alignment: AlignmentType.LEFT,
                children: runs
              })
            );
          } else if (typeof childBlock.text === "string") {
            // simple leaf directly inside cell
            paras.push(
              new Paragraph({
                alignment: AlignmentType.LEFT,
                children: [
                  new TextRun({
                    text: childBlock.text || "",
                    bold: rowIndex === 0
                  })
                ]
              })
            );
          } else {
            // fallback empty paragraph
            paras.push(new Paragraph({ children: [new TextRun("")] }));
          }
        }
      } else {
        // empty cell
        paras.push(new Paragraph({ children: [new TextRun("")] }));
      }

      // header shading for first row
      const shading = rowIndex === 0 ? { fill: "D9D9D9" } : undefined;

      return new TableCell({
        children: paras,
        shading
      });
    });

    return new TableRow({ children: cells });
  });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows
  });
};

// <-------------------------->

const getAlignment = (align) => {
  switch (align) {
  case "center":
    return AlignmentType.CENTER;
  case "right":
    return AlignmentType.RIGHT;
  case "justify":
    return AlignmentType.JUSTIFIED;
  default:
    return AlignmentType.LEFT;
  }
};

const downloadFile = async(url, fileName, setLoader) => {
  setLoader(true);
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to download file");
      
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
  
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setLoader(false);
    URL.revokeObjectURL(objectUrl); // Cleanup
  } catch (error) {
    console.error("Download failed:", error);
  }
};

export{
  exportSlateToDocx,
  downloadFile
};